import { OrdersModel, RefundModel } from '@pukka/api';
import { format, formatISO } from 'date-fns';
import findLast from 'lodash/findLast';
import flatMap from 'lodash/flatMap';
import groupBy from 'lodash/groupBy';
import sumBy from 'lodash/sumBy';

import { ITransactionRefundState } from './TransactionRefund.state';

type RefundAvailability = OrdersModel.RefundAvailability;
type OrderDetail = OrdersModel.OrderDetail;
type RefundPayload = RefundModel.RefundPayload;

interface Refund {
  categoryId: RefundAvailability['category_id'];
  categoryName: RefundAvailability['category_name'];
  discountPerItem: RefundAvailability['discount_per_item'];
  redemptionPerItem: RefundAvailability['redemption_per_item'];
  discounts: RefundAvailability['discounts'];
  itemId: RefundAvailability['item_id'];
  itemName: RefundAvailability['item_name'];
  itemImage: RefundAvailability['item_image'];
  itemVariantId: RefundAvailability['item_variant_id'];
  itemVariantName: RefundAvailability['item_variant_name'];
  itemVariantSku: RefundAvailability['item_variant_sku'];
  gratuityPerItem: RefundAvailability['gratuity_per_item'];
  grossPerItem: RefundAvailability['gross_per_item'];
  modifiers: RefundAvailability['modifiers'];
  gratuities: RefundAvailability['gratuities'];
  taxPerItem: RefundAvailability['tax_per_item'];
  trackStock: RefundAvailability['is_track_stock'];
  parentCheckoutId: RefundAvailability['parent_order_item_id'];
  parentCheckoutUuid: RefundAvailability['parent_order_item_uuid'];
  itemType: RefundAvailability['item_type'];
  bundleId: RefundAvailability['bundle_id'];
  pricePerItem: RefundAvailability['price_per_item'];
  netPerItem: RefundAvailability['net_per_item'];
  position: RefundAvailability['position'];
  salesTypeId: RefundAvailability['sales_type_id'];
  salesTypeName: RefundAvailability['sales_type_name'];
  maxQuantity: RefundAvailability['quantity'];
  isRefunded: boolean;
  refundAmount: number;
}

function extractForRefundBreakdown(transactionDetail: OrderDetail): Record<string, Refund[]> {
  // extract for refund breakdown
  const refundAvailabilities = transactionDetail.refund_availability;
  const refunds: Refund[] = (refundAvailabilities ?? []).map((refundAvailability) => ({
    categoryId: refundAvailability.category_id,
    categoryName: refundAvailability.category_name,
    discountPerItem: refundAvailability.discount_per_item,
    redemptionPerItem: refundAvailability.redemption_per_item,
    discounts: refundAvailability.discounts,
    itemId: refundAvailability.item_id,
    itemName: refundAvailability.item_name,
    itemImage: refundAvailability.item_image,
    itemVariantId: refundAvailability.item_variant_id,
    itemVariantName: refundAvailability.item_variant_name,
    itemVariantSku: refundAvailability.item_variant_sku,
    gratuityPerItem: refundAvailability.gratuity_per_item,
    grossPerItem: refundAvailability.gross_per_item,
    modifiers: refundAvailability.modifiers,
    gratuities: refundAvailability.gratuities,
    taxPerItem: refundAvailability.tax_per_item,
    trackStock: refundAvailability.is_track_stock,
    parentCheckoutId: refundAvailability.parent_order_item_id,
    parentCheckoutUuid: refundAvailability.parent_order_item_uuid,
    itemType: refundAvailability.item_type,
    bundleId: refundAvailability.bundle_id,
    pricePerItem: refundAvailability.price_per_item,
    netPerItem: refundAvailability.net_per_item,
    position: refundAvailability.position,
    salesTypeId: refundAvailability.sales_type_id,
    salesTypeName: refundAvailability.sales_type_name,
    // max quantity is maximum amount that can be refunded
    maxQuantity: refundAvailability.quantity,
    // is refunded is whether the item is included as refunded item.
    // if 0, it can't be refunded and must be excluded
    isRefunded: refundAvailability.quantity > 0,
    // default value is quantity if can be refunded
    refundAmount: refundAvailability.quantity > 0 ? refundAvailability.quantity : 0,
  }));

  return groupBy(refunds, (item) => item.salesTypeName);
}

function constructRefundItemsPromo(transactionDetail: OrderDetail, refundItems: ReturnType<typeof extractForRefundBreakdown>) {
  if (!transactionDetail.promos) {
    return [];
  }

  const allRefundItems = flatMap(Object.values(refundItems));
  let isChecked = false;

  return transactionDetail.promos.map((promo) => {
    promo.items?.forEach((item) => {
      const lastMatchingRefund = findLast(allRefundItems, (refundItem) => refundItem.parentCheckoutUuid === item.order_item_uuid);

      if (lastMatchingRefund) {
        isChecked = lastMatchingRefund.isRefunded;
      }
    });

    return {
      ...promo,
      isChecked,
    };
  });
}

type RefundItems = ReturnType<typeof extractForRefundBreakdown>;
type RefundItemsPromo = ReturnType<typeof constructRefundItemsPromo>;

const isPromoItem = (checkoutUuid: string, refundItemsPromo?: RefundItemsPromo) => {
  if (!refundItemsPromo) {
    return false;
  }

  const allPromoItems = flatMap(refundItemsPromo.map((p) => p.items || []));

  return allPromoItems.some((item) => item.order_item_uuid === checkoutUuid);
};

const calculatePromoItemPrice = (refundItem: Refund, refundItemsPromo: RefundItemsPromo) => {
  let promoPrice = 0;

  if (refundItemsPromo) {
    refundItemsPromo.forEach((promo) => {
      if (promo.items && promo.items.find((itemPromo) => itemPromo.order_item_uuid === refundItem.parentCheckoutUuid)) {
        let disc = 0;
        let discountRp = 0;

        if (!refundItem.maxQuantity) {
          // if all items have been refunded
          promoPrice = refundItem.netPerItem;
        } else {
          promoPrice = refundItem.refundAmount * (refundItem.pricePerItem - refundItem.discountPerItem);
        }

        // need this code since discount from promo is calculated in discount_per_item inside block refund_availibility,
        // but not included in discount_per_item inside discounts[].
        if (refundItem.discounts && refundItem.discounts.length > 0) {
          refundItem.discounts.forEach((discount) => {
            // discount Rp from promo is treated differently from discount Rp non promo
            if (discount.discount_type === 'cash') {
              // total discount Rp in block discounts
              discountRp += discount.discount_per_item;
              // total discount from promo
              disc = refundItem.discountPerItem - discountRp;

              if (!refundItem.maxQuantity) {
                // if all items have been refunded
                promoPrice = refundItem.netPerItem + discountRp;
              } else {
                promoPrice = refundItem.refundAmount * (refundItem.pricePerItem - disc);
              }
            }
          });
        }
      }
    });
  }

  return promoPrice;
};

const isFreeItem = (checkoutUuid: string, refundItemsPromo: RefundItemsPromo) => {
  let isFree = false;
  let idx = 0;

  if (refundItemsPromo) {
    while (idx < refundItemsPromo.length) {
      if (refundItemsPromo[idx].items && refundItemsPromo[idx].items.length > 0) {
        const itemPromo = refundItemsPromo[idx].items?.find((item) => item.order_item_uuid === checkoutUuid);

        if (itemPromo && itemPromo.promo_item_status === 'REWARD') {
          isFree = true;
          break;
        }
      }

      idx += 1;
    }
  }

  return isFree;
};

// get promo name for each promo item to be refunded
const getPromoName = (refundItem: Refund, refundItemsPromo: RefundItemsPromo) => {
  if (refundItemsPromo && refundItemsPromo.length > 0) {
    let promoName = '';
    let idx = 0;

    while (idx < refundItemsPromo.length) {
      const promoItem = refundItemsPromo[idx].items?.find((itemPromo) => itemPromo.order_item_uuid === refundItem.parentCheckoutUuid);

      if (promoItem) {
        promoName = refundItemsPromo[idx].promo_name;
        break;
      }

      idx += 1;
    }

    return promoName;
  }

  return '';
};

function calculateDiscountCash(discounts?: Refund['discounts']) {
  return sumBy(
    (discounts || []).filter((d) => d.discount_type === 'cash'),
    (d) => d.discount_per_item,
  );
}

const calculateItemPrice = (refundItem: Refund) => {
  const discountCash = calculateDiscountCash(refundItem.discounts);
  const baseItemPrice = refundItem.netPerItem + discountCash + refundItem.redemptionPerItem;
  const itemPrice = refundItem.maxQuantity ? refundItem.refundAmount * baseItemPrice : baseItemPrice;

  return Math.max(0, itemPrice);
};

const calculateItemPriceIncluded = (refundItem: Refund) => {
  const allRefunded =
    refundItem.netPerItem +
    calculateDiscountCash(refundItem.discounts) +
    refundItem.redemptionPerItem +
    refundItem.taxPerItem +
    refundItem.gratuityPerItem;

  const itemPrice = refundItem.maxQuantity ? refundItem.refundAmount * allRefunded : allRefunded;

  return Math.max(itemPrice, 0);
};

const calculatePriceAfterDiscountPercentage = (
  salesType: string,
  itemIdx: number,
  transactionDetail: OrderDetail,
  refundItems: RefundItems,
  refundItemsPromo: RefundItemsPromo,
) => {
  // salesType === "null" means no sales type. it should be "" for no sales type. remove this code when it is correct
  const keySalesType = salesType === 'null' ? '' : salesType;
  let itemPrice = 0;
  const item = refundItems[salesType][itemIdx];
  let discountPercentage = 0.0;

  // calculate discount percentage
  if (item.discounts && item.discounts.length > 0) {
    item.discounts.forEach((discount) => {
      if (discount.discount_type === 'percentage') {
        discountPercentage += discount.discount_percentage;
      }
    });
  }

  const orderItems = transactionDetail.order_items as OrdersModel.OrderItems;

  // non promo item price after percentage discount
  if (!isPromoItem(item.parentCheckoutUuid, refundItemsPromo)) {
    const price = orderItems[keySalesType]?.find((checkout) => checkout.uuid === item.parentCheckoutUuid)?.price ?? 0;

    itemPrice = (1 - discountPercentage / 100.0) * price;
  } else {
    // promo item price after reward and discount
    let promoItemPrice = 0;
    let rewardAmount = 0;

    promoItemPrice = orderItems[keySalesType].find((checkout) => checkout.uuid === item.parentCheckoutUuid)?.price ?? 0;

    const priceAfterDiscount = (1 - discountPercentage / 100.0) * promoItemPrice;

    if (transactionDetail.promos) {
      transactionDetail.promos.forEach((promo) => {
        const promoFound = promo.items && promo.items.find((promoItem) => promoItem.order_item_uuid === item.parentCheckoutUuid);

        if (promoFound) {
          if (promo.reward_discount_type === 'PERCENTAGE') {
            rewardAmount = (promo.reward_amount / 100.0) * promoItemPrice;
          } else {
            // free item or cash reward
            rewardAmount = promoFound.total_rewarded;
          }
        }
      });
    }

    // handle promo with reward discount 100%
    // will return 0 if reward > item price
    if (promoItemPrice > rewardAmount) {
      itemPrice = priceAfterDiscount - rewardAmount;
    }
  }

  return itemPrice;
};

const calculateDiscount = (
  discountAmount: number,
  transactionDetail: OrderDetail,
  refundItems: RefundItems,
  refundItemsPromo: RefundItemsPromo,
) => {
  let total = 0; // total discount Rp to be displayed on issue refund popup in Discount section
  let totalDisc = 0; // total discount Rp for checked non promo items
  let totalDiscPromo = 0; // total discount Rp of checked promo items
  let totalPrice = 0; // sum of displayed items price on popup issue refund
  let price = 0; // total price of non promo items to be refunded
  let totalPromoPrice = 0; // total price of promo items to be refunded
  let nonRefundQuantity = 0;

  // eslint-disable-next-line guard-for-in
  for (const salesType in refundItems) {
    // eslint-disable-next-line no-loop-func
    refundItems[salesType].forEach((refundItem, index) => {
      if (refundItem.isRefunded) {
        if (isPromoItem(refundItem.parentCheckoutUuid, refundItemsPromo)) {
          totalPromoPrice += calculatePromoItemPrice(refundItem, refundItemsPromo);
        } else if (!transactionDetail.include_gratuity_tax) {
          price += calculateItemPrice(refundItem) - refundItem.redemptionPerItem * refundItem.refundAmount;
        } else {
          price += calculateItemPriceIncluded(refundItem) - refundItem.redemptionPerItem * refundItem.refundAmount;
        }
      }

      // this calculation needed for discount Rp breakdown
      if (refundItem.isRefunded) {
        totalPrice +=
          calculatePriceAfterDiscountPercentage(salesType, index, transactionDetail, refundItems, refundItemsPromo) -
          refundItem.redemptionPerItem * refundItem.refundAmount;
      } else {
        // This is for partial discounts as we receive redeemAmount and maxQuantity as 0 in refundBreakdown , so we find quantity in checkouts and use it to find the price of item which is not redeemable/ refundable by deleting redemption item from it.
        nonRefundQuantity =
          (transactionDetail.order_items as OrdersModel.OrderItems)[salesType].find(
            (checkout) => checkout.uuid === refundItem.parentCheckoutUuid,
          )?.quantity ?? 0;
        totalPrice +=
          calculatePriceAfterDiscountPercentage(salesType, index, transactionDetail, refundItems, refundItemsPromo) -
          refundItem.redemptionPerItem * nonRefundQuantity;
      }

      totalDisc = (price / totalPrice) * discountAmount;
      totalDiscPromo = (totalPromoPrice / totalPrice) * discountAmount;
      total = totalDisc + totalDiscPromo;
    });
  }

  return total;
};

const getRewardName = (transactionDetail: OrderDetail) => {
  return transactionDetail.loyalty && transactionDetail.loyalty.type?.toLowerCase() === 'redemption'
    ? transactionDetail.loyalty.redemption?.reward_checkout_title || transactionDetail.loyalty.redemption?.reward
    : '';
};

const canBeRefunded = (refundItem: Refund, itemPrice: number) => {
  return ((!refundItem.refundAmount && itemPrice === 0) || (itemPrice > 0 && refundItem.refundAmount > 0)) && refundItem.maxQuantity > 0;
};

const isChecked = (salesType: string, idx: number, refundItems: RefundItems) => {
  return refundItems[salesType][idx].isRefunded;
};

const calculateRedeemAmount = (refundItems: RefundItems) => {
  let totalAmount = 0;

  // eslint-disable-next-line guard-for-in
  for (const salesType in refundItems) {
    totalAmount += refundItems[salesType]
      .filter((item) => item.isRefunded)
      .reduce((total, item) => {
        return total + item.redemptionPerItem * item.refundAmount;
      }, 0);
  }

  return totalAmount;
};

const calculateGratuity = (gratuityId: number, transactionDetail: OrderDetail, refundItems: RefundItems) => {
  const orderGratuities = transactionDetail.order_gratuities?.map((gratuity) => ({
    ...gratuity,
    gratuity_name: '',
    sales_type_id: 0,
    sales_type_name: '',
  }));

  const allRefundItems = flatMap(Object.values(refundItems));

  return sumBy(allRefundItems, (refundItem) => {
    if (!refundItem.isRefunded) {
      return 0;
    }

    return sumBy(refundItem.salesTypeId && refundItem.salesTypeId !== 0 ? refundItem.gratuities : orderGratuities, (gratuity) => {
      return gratuity.gratuity_id === gratuityId ? (gratuity.amount / 100) * refundItem.refundAmount * refundItem.netPerItem : 0;
    });
  });
};

const calculateTax = (taxId: number, transactionDetail: OrderDetail, refundItems: RefundItems) => {
  let total = 0;
  const allTaxes = transactionDetail.order_taxes.filter((tax) => tax.tax_id === taxId).map((tax) => tax.amount / 100.0);

  const allRefundItems = flatMap(Object.values(refundItems)).filter((r) => r.isRefunded);

  allTaxes.forEach((tax) => {
    allRefundItems.forEach((refundItem) => {
      total += tax * refundItem.refundAmount * (refundItem.netPerItem + refundItem.gratuityPerItem);
    });
  });

  return total;
};

const isRefundingTheLastItem = (refundItems: RefundItems, refundAvailability: RefundAvailability[] = []) => {
  const totalRefundedItems = sumBy(
    flatMap(Object.values(refundItems)).filter((r) => r.isRefunded),
    (r) => r.refundAmount,
  );

  const refundAvailabilityQuantity = sumBy(refundAvailability, (t) => {
    return t.price_per_item - t.discount_per_item === t.redemption_per_item ? 0 : t.quantity;
  });

  return totalRefundedItems === refundAvailabilityQuantity;
};

const calculateAmountToRefund = (transactionDetail: OrderDetail, refundItems: RefundItems, refundItemsPromo: RefundItemsPromo) => {
  let totalItems = 0;
  let totalDiscountCash = 0;
  let totalPromoPrice = 0;
  let totalDiscountCashPromoItem = 0;

  // eslint-disable-next-line guard-for-in
  for (const salesType in refundItems) {
    // eslint-disable-next-line no-loop-func
    refundItems[salesType].forEach((refundItem) => {
      if (!refundItem.isRefunded) {
        return;
      }

      if (!isPromoItem(refundItem.parentCheckoutUuid, refundItemsPromo)) {
        const itemPrice = calculateItemPrice(refundItem);

        totalDiscountCash += refundItem.maxQuantity
          ? itemPrice - refundItem.refundAmount * refundItem.netPerItem
          : itemPrice - refundItem.netPerItem;
        totalItems += itemPrice;
      } else {
        const promoPrice = calculatePromoItemPrice(refundItem, refundItemsPromo);

        totalDiscountCashPromoItem += refundItem.maxQuantity
          ? promoPrice - refundItem.refundAmount * refundItem.netPerItem
          : promoPrice - refundItem.netPerItem;
        totalPromoPrice += promoPrice;
      }
    });
  }

  const totalRounding = isRefundingTheLastItem(refundItems, transactionDetail.refund_availability)
    ? transactionDetail.total_rounding_amount
    : 0;

  const totalGratuities = sumBy(transactionDetail.order_gratuities, (gratuity) =>
    calculateGratuity(gratuity.gratuity_id, transactionDetail, refundItems),
  );

  const totalTaxes = sumBy(transactionDetail.order_taxes, (tax) => calculateTax(tax.tax_id, transactionDetail, refundItems));

  return totalItems - totalDiscountCash + (totalPromoPrice - totalDiscountCashPromoItem) + totalGratuities + totalTaxes + totalRounding;
};

const getPromoIdx = (refundItem: Refund, refundItemsPromo?: RefundItemsPromo) => {
  if (refundItemsPromo && refundItemsPromo.length > 0) {
    let promoIdx: number | null = null;
    let idx = 0;

    while (idx < refundItemsPromo.length) {
      const promoItem = refundItemsPromo[idx].items?.find((itemPromo) => itemPromo.order_item_uuid === refundItem.parentCheckoutUuid);

      if (promoItem) {
        promoIdx = idx;
        break;
      }

      idx += 1;
    }

    return promoIdx;
  }

  return null;
};

// calculate minimum amount requirement of promo
const countPromoRequirement = (requirementId: number, refundItemsPromo: RefundItemsPromo) => {
  let countRequirement = 0;
  let amountRequirement = 0;
  let minReq = 0;

  if (refundItemsPromo && refundItemsPromo.length > 0) {
    refundItemsPromo.forEach((promo) => {
      promo.items?.forEach((itemPromo) => {
        if (itemPromo.promo_requirement_id === requirementId) {
          amountRequirement += itemPromo.amount;
        }
      });

      if (promo.items?.find((item) => item.promo_requirement_id === requirementId)) {
        countRequirement += promo.count;
      }
    });

    minReq = amountRequirement / countRequirement;
  }

  return minReq;
};

// calculate minimum amount reward
const countPromoReward = (rewardId: number, refundItemsPromo: RefundItemsPromo) => {
  let countReward = 0;
  let amountReward = 0;
  let minReward = 0;

  if (refundItemsPromo && refundItemsPromo.length > 0) {
    refundItemsPromo.forEach((promo) => {
      promo.items?.forEach((itemPromo) => {
        if (itemPromo.promo_reward_id === rewardId) {
          amountReward += itemPromo.amount;
        }
      });

      if (promo.items?.find((item) => item.promo_reward_id === rewardId)) {
        countReward += promo.count;
      }
    });

    minReward = amountReward / countReward;
  }

  return minReward;
};

const getItemAmounts = (refundItems: RefundItems) => {
  const amounts: Record<string, (number | string)[]> = {};

  Object.keys(refundItems).forEach((salesType) => {
    amounts[salesType] = refundItems[salesType].map((refundItem) => refundItem.refundAmount);
  });

  return amounts;
};

const shouldShowCheckbox = (
  refundItem: Refund,
  salesType: string,
  idx: number,
  isPromo: boolean,
  isFree: boolean,
  promoItemPrice: number,
  canRefund: boolean,
  disableRefund?: (salesType: string, idx: number) => void,
) => {
  let show: boolean;

  if (refundItem.pricePerItem - refundItem.discountPerItem === refundItem.redemptionPerItem) {
    show = false;

    if (refundItem.isRefunded) {
      disableRefund?.(salesType, idx);
    }
  } else if (isPromo) {
    show = !isFree && refundItem.maxQuantity > 0 && promoItemPrice > 0;
  } else {
    show = canRefund;
  }

  return show;
};

const nullIfZero: (value: number | null) => number | null = (value) => {
  if (!value || value === 0) {
    return null;
  }

  return value;
};

const isRequirementRefunded = (checkoutUuid: string, refundItems: RefundItems, refundItemsPromo: RefundItemsPromo) => {
  if (!refundItemsPromo || refundItemsPromo.length === 0) {
    return false;
  }

  const refundedCheckoutUuids = Object.keys(refundItems)
    .map((salesType) => refundItems[salesType])
    .reduce((merged, items) => merged.concat(items), [])
    .filter((item) => item.isRefunded)
    .map((item) => item.parentCheckoutUuid);

  return refundItemsPromo.some((promo) => {
    const isRelevantPromo = promo.items?.some((item) => item.promo_item_status === 'REWARD' && item.order_item_uuid === checkoutUuid);

    if (!isRelevantPromo) {
      return false;
    }

    return promo.items
      ?.filter((item) => item.promo_item_status === 'REQUIREMENT')
      .map((item) => item.order_item_uuid)
      .every((uuid) => refundedCheckoutUuids.indexOf(uuid) !== -1);
  });
};

const calculateTaxable = (taxId: number, transactionDetail: OrderDetail, refundItems: RefundItems) => {
  let total = 0;

  transactionDetail.order_taxes?.forEach((tax) => {
    if (tax.id === taxId) {
      // eslint-disable-next-line guard-for-in
      for (const salesType in refundItems) {
        for (let i = 0; i < refundItems[salesType].length; i += 1) {
          const refundItem = refundItems[salesType][i];

          if (refundItem.isRefunded) {
            total += refundItem.refundAmount * (refundItem.netPerItem + refundItem.gratuityPerItem);
          }
        }
      }
    }
  });

  return total;
};

const getFullSubTotalRefunded = (transactionDetail: OrderDetail) => {
  // when gratuity and tax is included
  // sub_total will be the same with the final amount
  // hence we dont need to do any special calculation
  if (transactionDetail.include_gratuity_tax) {
    return transactionDetail.total_refund;
  }

  let fullSubTotalRefund = 0;

  for (let i = 0; i < transactionDetail.order_refunds.length; i += 1) {
    const paymentRefund = transactionDetail.order_refunds?.[i];
    const totalTaxes = paymentRefund?.order_taxes.map((e) => e.total).reduce((first, second) => first + second, 0);
    const totalGratuity = paymentRefund?.order_gratuities.map((e) => e.total).reduce((first, second) => first + second, 0);
    const subTotalRefund = paymentRefund.refund_amount - Math.abs(totalTaxes + totalGratuity);

    fullSubTotalRefund += subTotalRefund;
  }

  return fullSubTotalRefund;
};

const calculateLoyaltyRefundedPoint = (transactionDetail: OrderDetail, currentSubTotalRefund: number) => {
  if (!transactionDetail.loyalty) {
    return 0;
  }

  const parentSubTotal = transactionDetail.subtotal;
  const previousFullSubTotalRefunded = getFullSubTotalRefunded(transactionDetail);
  const earningRule = transactionDetail.loyalty.earning_rule;
  // TODO: CEPP-441. loyalty is not mapped.
  //  This does not crash in TransactionRefundModal because there is a null check
  // const refundedPoint = transactionDetail.order_refunds
  //   .map(e => {
  //     return e.loyalty ? e.loyalty.point_correction : 0;
  //   })
  //   .reduce((first, second) => first + second, 0);
  const refundedPoint = 0;
  const currentAvailablePoint = transactionDetail.loyalty.new_earned_points - Math.abs(refundedPoint);
  const finalCustomerSpendingAmount = parentSubTotal - previousFullSubTotalRefunded - currentSubTotalRefund;

  return (
    parseInt((Math.round(finalCustomerSpendingAmount) / earningRule.amount) as unknown as string, 10) * earningRule.point -
    currentAvailablePoint
  );
};

const calculateLoyaltyRefundedPointForItemRule = (transactionDetail: OrderDetail, refundItems: RefundItems) => {
  if (!transactionDetail.loyalty) {
    return 0;
  }

  let refundPoints = 0;
  const itemRulePoints = transactionDetail.loyalty.earning_rule.point;

  // eslint-disable-next-line guard-for-in
  for (const salesType in refundItems) {
    for (let i = 0; i < refundItems[salesType].length; i += 1) {
      const refundItem = refundItems[salesType][i];
      const isProgramItem = (transactionDetail.order_items as OrdersModel.OrderItems)[salesType].find(
        (checkout) => checkout.uuid === refundItem.parentCheckoutUuid,
      )?.is_program_item;

      if (isProgramItem && refundItem.isRefunded) {
        refundPoints -= itemRulePoints * refundItem.refundAmount;
      }
    }
  }

  return refundPoints;
};

const isRefundBreakdown = (orderDetail: OrderDetail) => {
  if (orderDetail.order_refunds && orderDetail.order_refunds.length > 0) {
    const hasOldRefund = orderDetail.order_refunds.some((refund) => !refund.is_refund_breakdown);

    return !hasOldRefund;
  }

  return !!orderDetail.refund_availability && orderDetail.refund_availability.length > 0;
};

const generateRefundPayload = (State: ITransactionRefundState) => {
  const { refundItems, refundItemsPromo, transactionDetail, reasonForRefund, otherReasonForRefund } = State;

  if (!transactionDetail) {
    return null;
  }

  // construct items
  const itemCallbacks: RefundModel.Item[] = [];

  // eslint-disable-next-line guard-for-in
  for (const salesType in refundItems) {
    refundItems[salesType].forEach((refundItem) => {
      const itemDiscounts = refundItem.discounts?.map((discount) => {
        return {
          discount_id: discount.discount_id,
          discount_name: discount.discount_name,
          discount_type: discount.discount_type,
          discount_cash: discount.discount_cash,
          discount_percentage: discount.discount_percentage,
          discount_amount: -refundItem.refundAmount * discount.discount_per_item,
        };
      });

      // items - modifiers
      const itemModifiers = (refundItem.modifiers ?? []).map((modifier) => {
        return {
          ...modifier,
          modifier_option_price: modifier.price,
          gross_sales: -refundItem.refundAmount * modifier.gross_per_item,
          net_sales: -refundItem.refundAmount * modifier.net_per_item,
          discount_amount: -refundItem.refundAmount * modifier.discount_per_item,
          redeem_amount: -refundItem.refundAmount * modifier.redeem_per_item,
          modifier_discounts: modifier.modifier_discounts?.map((modifierDiscount) => ({
            ...modifierDiscount,
            discount_amount: -refundItem.refundAmount * modifierDiscount.discount_per_item,
          })),
        };
      });
      const itemGratuities =
        refundItem.salesTypeId && refundItem.salesTypeId !== 0
          ? (refundItem.gratuities ?? []).map((gratuity) => {
              return {
                gratuity_percentage: gratuity.amount,
                gratuity_amount: -((gratuity.amount / 100) * refundItem.netPerItem * refundItem.refundAmount),
                gratuity_id: gratuity.gratuity_id,
                sales_type_id: gratuity.sales_type_id,
                sales_type_name: gratuity.sales_type_name,
                gratuity_name: gratuity.gratuity_name,
              };
            })
          : (transactionDetail.order_gratuities ?? []).map((gratuity) => {
              return {
                gratuity_percentage: gratuity.amount,
                gratuity_amount: -((gratuity.amount / 100) * refundItem.netPerItem * refundItem.refundAmount),
                gratuity_id: gratuity.gratuity_id,
                sales_type_id: 0,
                gratuity_name: gratuity.name,
              };
            });

      const _isRequirementRefunded = isRequirementRefunded(refundItem.parentCheckoutUuid, refundItems, refundItemsPromo);

      if (refundItem.isRefunded || _isRequirementRefunded) {
        itemCallbacks.push({
          category_id: refundItem.categoryId,
          category_name: refundItem.categoryName,
          client_price: refundItem.pricePerItem,
          discount_amount: -refundItem.refundAmount * refundItem.discountPerItem,
          redeem_amount: -refundItem.refundAmount * refundItem.redemptionPerItem,
          discounts: itemDiscounts,
          gratuity: -refundItem.refundAmount * refundItem.gratuityPerItem,
          gratuities: itemGratuities,
          gross_sales: -refundItem.refundAmount * refundItem.grossPerItem,
          item_id: nullIfZero(refundItem.itemId),
          item_image: refundItem.itemImage,
          item_name: refundItem.itemName,
          item_variant_id: nullIfZero(refundItem.itemVariantId),
          item_variant_name: refundItem.itemVariantName,
          item_variant_sku: refundItem.itemVariantSku,
          modifiers: itemModifiers,
          net_sales: -refundItem.refundAmount * refundItem.netPerItem,
          parent_checkout_id: refundItem.parentCheckoutId,
          parent_checkout_uuid: refundItem.parentCheckoutUuid,
          item_type: refundItem.itemType,
          bundle_id: refundItem.bundleId,
          position: refundItem.position,
          quantity: -refundItem.refundAmount,
          tax: -refundItem.refundAmount * refundItem.taxPerItem,
          track_stock: refundItem.trackStock,
          sales_type_id: refundItem.salesTypeId,
          sales_type_name: refundItem.salesTypeName,
          from_promo: isPromoItem(refundItem.parentCheckoutUuid, refundItemsPromo),
        });
      }
    });
  }

  // construct discounts
  const discounts = (transactionDetail.order_discounts ?? []).map((discount) => {
    return {
      discount_amount: -calculateDiscount(discount.discount_amount, transactionDetail, refundItems, refundItemsPromo),
      discount_cash: discount.discount_cash,
      discount_id: discount.discount_id,
      discount_name: discount.discount_name,
      discount_type: discount.discount_type,
    };
  });

  // construct gratuities
  const gratuities: RefundModel.Gratuity[] = [];

  transactionDetail.order_gratuities?.forEach((gratuity) => {
    const total = calculateGratuity(gratuity.gratuity_id, transactionDetail, refundItems);

    if (total !== 0) {
      gratuities.push({
        gratuity_amount: -total,
        gratuity_id: gratuity.gratuity_id,
        gratuity_name: gratuity.name,
        gratuity_percentage: gratuity.amount,
      });
    }
  });

  // construct taxes
  const taxes: RefundModel.Tax[] = [];

  transactionDetail.order_taxes?.forEach((tax) => {
    taxes.push({
      tax_amount: -calculateTax(tax.tax_id, transactionDetail, refundItems),
      tax_id: tax.tax_id,
      tax_name: tax.name,
      tax_percentage: tax.amount,
      taxable_amount: -calculateTaxable(tax.id, transactionDetail, refundItems),
    });
  });

  let totalDiscount = 0;
  let totalGratuity = 0;
  let totalGrossSales = 0;
  let totalNetSales = 0;
  let totalTax = 0;
  let totalRedeemAmount = 0;
  let subtotal = 0;

  // eslint-disable-next-line guard-for-in
  for (const st in refundItems) {
    for (let i = 0; i < refundItems[st].length; i += 1) {
      const refundItem = refundItems[st][i];

      if (refundItem.isRefunded) {
        totalDiscount += refundItem.refundAmount * refundItem.discountPerItem;
        totalGratuity += refundItem.refundAmount * refundItem.gratuityPerItem;
        totalGrossSales += refundItem.refundAmount * refundItem.grossPerItem;
        totalNetSales += refundItem.refundAmount * refundItem.netPerItem;
        totalTax += refundItem.refundAmount * refundItem.taxPerItem;
        totalRedeemAmount += refundItem.refundAmount * refundItem.redemptionPerItem;
        subtotal +=
          refundItem.refundAmount * refundItem.pricePerItem -
          (refundItem.refundAmount * refundItem.discountPerItem + refundItem.refundAmount * refundItem.redemptionPerItem);
      }
    }
  }

  let totalCollected = totalNetSales + totalGratuity + totalTax;
  let refundedRoundingAmount = 0;

  if (isRefundingTheLastItem(refundItems, transactionDetail.refund_availability)) {
    totalCollected += transactionDetail.total_rounding_amount ?? 0;
    refundedRoundingAmount = -(transactionDetail.total_rounding_amount ?? 0);
  }

  let loyalty: RefundModel.Loyalty | null = null;

  if (transactionDetail.loyalty) {
    loyalty = {
      new_earned_points: 0,
      program_id: transactionDetail.loyalty.program_id,
      member_since: transactionDetail.loyalty.member_since,
      earning_rule: transactionDetail.loyalty.earning_rule,
      member_id: transactionDetail.loyalty.member_id,
      member_uniq_id: transactionDetail.loyalty.member_uniq_id,
      member_guid: transactionDetail.loyalty.member_guid,
    };

    if (transactionDetail.loyalty.type?.toUpperCase() === 'REDEMPTION') {
      const { redemption } = transactionDetail.loyalty;

      loyalty.redemption = {
        redeem_points: redemption?.redeem_points,
        redeem_amount: -totalRedeemAmount,
        reward_type: redemption?.reward_type,
        reward_id: redemption?.reward_id,
        reward: redemption?.reward,
        reward_checkout_title: redemption?.reward_checkout_title,
        discount: redemption?.discount,
        discount_type: redemption?.discount_type,
      };
    }

    if (transactionDetail.loyalty.type?.toUpperCase() === 'EARNING') {
      if (transactionDetail.loyalty.earning_rule?.type === 'SPENDING') {
        loyalty.new_earned_points = calculateLoyaltyRefundedPoint(transactionDetail, subtotal);
      } else {
        loyalty.new_earned_points = calculateLoyaltyRefundedPointForItemRule(transactionDetail, refundItems);
      }
    }
  }

  const newPromos: RefundModel.Promo[] = [];

  (refundItemsPromo || []).forEach((refundPromo) => {
    if (refundPromo.isChecked) {
      let refundCount = 0;
      let refundAmount = 0;
      let refundTotalReward = 0;
      let refundGross = 0;
      let _canBeRefunded = false;

      const newItems: RefundModel.PromoItem[] = [];

      refundPromo.items?.forEach((item) => {
        const itemCallback = itemCallbacks.find((newItem) => newItem.parent_checkout_uuid === item.order_item_uuid);

        if (itemCallback) {
          refundAmount = -itemCallback.quantity;
          refundCount = refundAmount / (item.amount / refundPromo.count);
          refundTotalReward = refundAmount * (item.total_rewarded / item.amount);
          refundGross += refundTotalReward;
          _canBeRefunded = true;

          const promoItem: RefundModel.PromoItem = {
            amount: refundAmount,
            category_id: item.category_id,
            category_name: item.category_name,
            item_id: item.item_id,
            item_name: item.item_name,
            item_variant_id: item.item_variant_id,
            item_variant_name: item.item_variant_name,
            total_rewarded: -refundTotalReward,
            checkout_id: item.order_item_id,
            checkout_uuid: item.order_item_uuid,
            promo_item_status: item.promo_item_status,
            promo_requirement_id: item.promo_requirement_id,
            promo_reward_id: item.promo_reward_id,
          };

          newItems.push(promoItem);
        }
      });

      if (_canBeRefunded) {
        const data: RefundModel.Promo = {
          business_id: transactionDetail.business_id,
          count: refundCount,
          gross: -refundGross,
          items: newItems,
          outlet_id: transactionDetail.outlet_id,
          promo_id: refundPromo.promo_id,
          promo_name: refundPromo.promo_name,
          refunded_from: refundPromo.items?.find((x) => x.order_item_uuid)?.order_promo_id,
          reward_amount: refundPromo.reward_amount,
          reward_discount_type: refundPromo.reward_discount_type,
          promo_type_id: refundPromo.promo_type_id,
        };

        newPromos.push(data);
      }
    }
  });

  const refundPayload: RefundPayload = {
    amount: Math.round(transactionDetail.total_collected_amount - transactionDetail.total_refund),
    client_created_at: formatISO(new Date()),
    collected_by: transactionDetail.collector_name,
    collector_name: transactionDetail.collector_name,
    created_at: transactionDetail.created_at,
    creator_name: transactionDetail.creator_name,
    custom_reason: reasonForRefund === 'Other' ? otherReasonForRefund : '',
    discounts,
    gratuities,
    is_refund_breakdown: isRefundBreakdown(transactionDetail),
    is_refund_transaction: true,
    items: itemCallbacks,
    loyalty,
    payment_id: transactionDetail.id,
    payment_uuid: transactionDetail.parent_order_uuid,
    promos: newPromos,
    reason: reasonForRefund !== 'Other' ? reasonForRefund : otherReasonForRefund,
    refund_amount: totalNetSales,
    subtotal: -subtotal,
    taxes,
    total_collected: -totalCollected,
    total_discount: -totalDiscount,
    total_gratuity: -totalGratuity,
    total_gross_sales: -totalGrossSales,
    total_net_sales: -totalNetSales,
    total_redeem_amount: -totalRedeemAmount,
    total_rounding_amount: refundedRoundingAmount,
    total_tax: -totalTax,
  };

  return { refund: refundPayload, outletId: transactionDetail.outlet_id };
};

function caseInsensitiveSort(s1: string, s2: string) {
  const s1lower = s1.toLowerCase();
  const s2lower = s2.toLowerCase();

  // eslint-disable-next-line no-nested-ternary
  return s1lower > s2lower ? 1 : s1lower < s2lower ? -1 : 0;
}

function sortOrderItems(orderItems: OrdersModel.OrderItems) {
  const NO_SALES_TYPE_NAME = 'No Sales Type';
  const orderItemsBySalesType: OrdersModel.OrderItems = {};

  Object.keys(orderItems)
    .filter((salesType) => salesType !== NO_SALES_TYPE_NAME)
    .sort(caseInsensitiveSort)
    .forEach((salesType: string) => {
      orderItemsBySalesType[salesType] = orderItems[salesType];
    });

  const orderItemsWithNoSalesType = orderItems[NO_SALES_TYPE_NAME];

  return orderItemsWithNoSalesType
    ? {
        ...orderItemsBySalesType,
        [NO_SALES_TYPE_NAME]: orderItemsWithNoSalesType,
      }
    : orderItemsBySalesType;
}

function breakdownRefundedItems(orderRefunds: OrdersModel.OrderRefund[]) {
  const res: [string, string | number, string, string, string][] = [];

  orderRefunds.forEach((orderRefund) => {
    const { refunded_by: refundedBy, refunded_reason: refundedReason, refunded_at: refundedAt, order_items: orderItems } = orderRefund;

    const date = format(new Date(refundedAt), 'd MMM yyyy HH:mm');

    Object.values(orderItems).forEach((itemsBySalesType) => {
      itemsBySalesType.forEach((item) => {
        res.push([item.item_name, Math.abs(item.quantity), refundedReason, refundedBy, date]);
      });
    });
  });

  return res;
}

export type { Refund, RefundItems, RefundItemsPromo };

export {
  extractForRefundBreakdown,
  constructRefundItemsPromo,
  isPromoItem,
  calculateDiscountCash,
  calculatePromoItemPrice,
  isFreeItem,
  getPromoName,
  calculateItemPrice,
  calculateItemPriceIncluded,
  calculateDiscount,
  getRewardName,
  canBeRefunded,
  isChecked,
  calculateRedeemAmount,
  calculateGratuity,
  calculateTax,
  isRefundingTheLastItem,
  calculateAmountToRefund,
  getPromoIdx,
  countPromoRequirement,
  countPromoReward,
  getItemAmounts,
  shouldShowCheckbox,
  calculatePriceAfterDiscountPercentage,
  nullIfZero,
  isRequirementRefunded,
  calculateTaxable,
  calculateLoyaltyRefundedPoint,
  calculateLoyaltyRefundedPointForItemRule,
  isRefundBreakdown,
  generateRefundPayload,
  getFullSubTotalRefunded,
  sortOrderItems,
  breakdownRefundedItems,
};
